import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {AnalyticsVisualizationOutlierSetting as OutlierSetting} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {OutlierSettingChartParameters} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {
  OUTLIER_SETTING_TO_READABLE,
  NULL_OUTLIER_SETTING,
  OUTLIER_TOOLTIP_INFO,
} from 'ui-library/charting/statistics/outlier_detection/OutlierDetectionConstants';
import {GRAY_DARK} from 'ui-library/Colors';
import Popover from 'ui-library/floating-ui/Popover';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import LabeledSlider from 'ui-library/inputs/LabeledSlider';

import {getOutlierSetting} from '../../data/getOutlierSetting';
import {StringValueDropdownSelector} from '../form-components/DropdownSelector';
import {VisualizationConfigEditorComponentProps} from '../VisualizationConfigEditorTypes';

type OutlierConfig = NonNullable<
  ExtractByTypename<
    AnalyticsVisualization['parameters'],
    'AnalyticsVisualizationScatterPlotParametersNode'
  >['outlierConfig']
>;

const ORDERED_OUTLIER_SETTINGS: Array<OutlierSetting> = [OutlierSetting.DETECT, OutlierSetting.EXCLUDE];
export const SENSITIVITY_TO_Q_VALUE_MAPPINGS: {[key: number]: number} = {
  0: 0.001,
  1: 0.002,
  2: 0.005,
  3: 0.01,
  4: 0.02,
  5: 0.05,
  6: 0.1,
};
export const Q_VALUE_TO_SENSITIVITY_MAPPINGS: {[key: number]: number} = {
  0.001: 0,
  0.002: 1,
  0.005: 2,
  0.01: 3,
  0.02: 4,
  0.05: 5,
  0.1: 6,
};
const DEFAULT_SENSITIVITY = 3;

export type OutlierConfigEditorProps =
  VisualizationConfigEditorComponentProps<OutlierSettingChartParameters> & {
    errorMessage?: string | null;
  };

export default function OutlierConfigEditor({
  parameters,
  onUpdateParameters,
  errorMessage,
  isDisabled = false,
}: OutlierConfigEditorProps): JSX.Element {
  const [outlierConfig, setOutlierConfig] = useState<OutlierConfig | null>(parameters.outlierConfig);
  const [sensitivity, setSensitivity] = useState(
    parameters.outlierConfig?.qValue
      ? (Q_VALUE_TO_SENSITIVITY_MAPPINGS[parameters.outlierConfig.qValue] ?? DEFAULT_SENSITIVITY)
      : DEFAULT_SENSITIVITY
  );
  const debouncedSensitivity = useDebounce(sensitivity, 300);

  useEffect(() => {
    setOutlierConfig(parameters.outlierConfig);
  }, [parameters]);

  useEffect(
    () => {
      const newQValue = SENSITIVITY_TO_Q_VALUE_MAPPINGS[debouncedSensitivity] ?? 0.01;
      if (parameters.outlierConfig != null && parameters.outlierConfig.qValue !== newQValue) {
        setOutlierConfig({
          ...parameters.outlierConfig,
          qValue: newQValue,
        });
        onUpdateParameters({
          ...parameters,
          outlierConfig: {
            ...parameters.outlierConfig,
            qValue: newQValue,
          },
        });
      }
    },
    [onUpdateParameters, parameters, debouncedSensitivity] // Only call effect if debounced search term changes
  );

  function handleUpdateOutlierSetting(newOutlierSetting: OutlierSetting | null): void {
    if (getOutlierSetting(parameters.outlierConfig) !== newOutlierSetting) {
      onUpdateParameters({
        ...parameters,
        outlierConfig: newOutlierSetting
          ? {
              __typename: 'OutlierConfigNode',
              outlierSetting: newOutlierSetting,
              // We preserve the old qValue if a user goes from detect/exclude -> no detection -> detect/exclude
              qValue: SENSITIVITY_TO_Q_VALUE_MAPPINGS[debouncedSensitivity],
            }
          : null,
      });
    }
  }

  return (
    <div className="u-input-spacing-top">
      <FieldLabel label="Outliers" />
      <Popover renderContent={() => OUTLIER_TOOLTIP_INFO}>
        <span>
          <BenchlingIcon className={css(styles.outlierInfo)} icon="more-info" />
        </span>
      </Popover>

      <div className="u-vertical-center-items">
        <div className="u-flex-grow">
          <StringValueDropdownSelector
            allowNull={true}
            errorMessage={errorMessage}
            onChange={handleUpdateOutlierSetting}
            searchable={false}
            selectedValue={getOutlierSetting(parameters.outlierConfig)}
            values={ORDERED_OUTLIER_SETTINGS}
            valueToReadable={(outlierSetting) => OUTLIER_SETTING_TO_READABLE[outlierSetting]}
            nullOptionName={NULL_OUTLIER_SETTING}
            isDisabled={isDisabled}
          />
        </div>
      </div>

      {getOutlierSetting(outlierConfig) != null && !isDisabled && (
        <div className="u-input-spacing-top">
          <FieldLabel label="Sensitivity" />
          <LabeledSlider
            min={0}
            minLabel={
              <>
                Fewer
                <br />
                outliers
              </>
            }
            max={6}
            maxLabel={
              <>
                More
                <br />
                outliers
              </>
            }
            onChange={setSensitivity}
            step={1}
            value={sensitivity}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  outlierInfo: {
    color: GRAY_DARK,
    marginLeft: 3,
    marginTop: 3,
  },
});
